import React from "react";
import { Draggable } from "@hello-pangea/dnd";
import { makeStyles } from "@material-ui/core/styles";
import { Avatar, IconButton, Tooltip, Typography } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import { i18n } from "../../translate/i18n";
import getContactDisplayName from "../../helpers/contactDisplayName";
import { draggableIdFor } from "./boardState";

// feat_395 — ficha del Tablero CRM con forma de burbuja de chat (WhatsApp).
// Tamaños desde theme.spacing y el avatar desde la densidad (feat_328).

const isDark = theme => theme.palette.type === "dark";

const useStyles = makeStyles(theme => ({
  card: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.25),
    marginBottom: theme.spacing(0.75),
    borderRadius: theme.spacing(1),
    borderTopLeftRadius: 0,
    background: isDark(theme) ? "#202c33" : "#ffffff",
    color: isDark(theme) ? "#e9edef" : "#111b21",
    boxShadow: "0 1px 0.5px rgba(11,20,26,.13)",
    cursor: "pointer",
    userSelect: "none",
    position: "relative",
    outline: "none",
    "&:hover": { background: isDark(theme) ? "#2a3942" : "#f5f6f6" },
    "&:focus-visible": { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
  },
  dragging: {
    boxShadow: "0 8px 20px rgba(0,0,0,.25)",
    transform: "rotate(1deg)",
  },
  avatar: {
    width: props => props.avatarSize,
    height: props => props.avatarSize,
    flexShrink: 0,
  },
  body: { flex: 1, minWidth: 0 },
  topRow: { display: "flex", alignItems: "baseline", gap: theme.spacing(0.5) },
  name: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  menuBtn: {
    flexShrink: 0,
    padding: 2,
    marginRight: -4,
    color: isDark(theme) ? "#8696a0" : "#667781",
  },
  time: {
    flexShrink: 0,
    fontSize: "0.75em",
    color: isDark(theme) ? "#8696a0" : "#667781",
  },
  number: {
    fontSize: "0.75em",
    color: isDark(theme) ? "#8696a0" : "#667781",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  message: {
    fontSize: "0.85em",
    color: isDark(theme) ? "#aebac1" : "#3b4a54",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
    marginTop: theme.spacing(0.25),
  },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  operator: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    fontSize: "0.72em",
    color: isDark(theme) ? "#8696a0" : "#667781",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  operatorIcon: { fontSize: "1.2em" },
  badge: {
    flexShrink: 0,
    minWidth: theme.spacing(2.5),
    height: theme.spacing(2.5),
    padding: theme.spacing(0, 0.75),
    borderRadius: theme.spacing(1.25),
    background: "#25d366",
    color: "#111b21",
    fontSize: "0.72em",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

export const relativeTime = (iso, now) => {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return i18n.t("crmBoard.timeNow");
  if (min < 60) return i18n.t("crmBoard.timeMinutes", { count: min });
  const h = Math.floor(min / 60);
  if (h < 24) return i18n.t("crmBoard.timeHours", { count: h });
  return i18n.t("crmBoard.timeDays", { count: Math.floor(h / 24) });
};

const senderPrefix = senderType => {
  if (senderType === "advisor") return i18n.t("crmBoard.prefixAdvisor");
  if (senderType === "ai") return i18n.t("crmBoard.prefixAi");
  if (senderType === "system") return i18n.t("crmBoard.prefixSystem");
  return "";
};

const BoardCard = ({ card, index, avatarSize, now, onOpen, onMenu }) => {
  const classes = useStyles({ avatarSize });
  const contact = card.contact || {};
  const ticket = card.ticket || {};
  const name = getContactDisplayName(contact, { fallback: "—" });
  const lastMessage = ticket.lastMessage
    ? `${senderPrefix(ticket.lastMessageSenderType)}${ticket.lastMessage}`
    : i18n.t("crmBoard.noMessages");
  const operator = ticket.id && ticket.userName ? ticket.userName : i18n.t("crmBoard.unassigned");
  const absolute = card.lastActivityAt ? new Date(card.lastActivityAt).toLocaleString() : "";

  return (
    <Draggable draggableId={draggableIdFor(contact.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`${classes.card} ${snapshot.isDragging ? classes.dragging : ""}`}
          style={provided.draggableProps.style}
          onClick={() => onOpen(card)}
          onKeyDown={e => {
            if (e.key === "Enter") onOpen(card);
          }}
          data-contact-id={contact.id}
        >
          <Avatar className={classes.avatar} src={contact.profilePicUrl || undefined}>
            {name.charAt(0).toUpperCase()}
          </Avatar>
          <div className={classes.body}>
            <div className={classes.topRow}>
              <Typography component="span" variant="body2" className={classes.name}>
                {name}
              </Typography>
              <Tooltip title={absolute} arrow>
                <span className={classes.time}>{relativeTime(card.lastActivityAt, now)}</span>
              </Tooltip>
              {onMenu && (
                // Funnel tanda 4 — menú de la ficha. Un <button> no inicia el
                // drag (dnd ignora elementos interactivos); el click/Enter se
                // cortan acá para no abrir el flotante.
                <IconButton
                  size="small"
                  className={classes.menuBtn}
                  aria-label={i18n.t("crmBoard.cardActions")}
                  data-card-menu={contact.id}
                  onClick={e => {
                    e.stopPropagation();
                    onMenu(card, e.currentTarget);
                  }}
                  onKeyDown={e => e.stopPropagation()}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )}
            </div>
            {/* El número se muestra tal cual lo devuelve el backend: para quien
                no puede ver teléfonos ya viene enmascarado (feat_384). */}
            {contact.number && !contact.isGroup && (
              <div className={classes.number}>{contact.number}</div>
            )}
            <div className={classes.message}>{lastMessage}</div>
            <div className={classes.footer}>
              <span className={classes.operator}>
                <PersonOutlineIcon className={classes.operatorIcon} />
                {operator}
              </span>
              {card.openTickets > 0 && (
                <Tooltip title={i18n.t("crmBoard.openTickets", { count: card.openTickets })} arrow>
                  <span className={classes.badge}>{card.openTickets}</span>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default React.memo(BoardCard);
